import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/axios';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const MovieAnalytics = () => {
  const theme = useTheme();

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['movieAnalytics'],
    queryFn: async () => {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/api/admin/analytics/movies', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: true // Refresh when tab becomes active
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Format data for charts
  const monthlyData = analytics?.monthlyUploads?.map(item => ({
    name: `${item._id.year}-${item._id.month}`,
    uploads: item.count
  })) || [];

  const categoryData = analytics?.moviesByCategory || [];
  const genreData = analytics?.moviesByGenre || [];

  // Generate daily view data from popular movies
  const viewsData = analytics?.popularMovies
    ?.map(movie => ({
      date: new Date().toLocaleDateString(),
      views: movie.views,
      title: movie.title.length > 15 ? `${movie.title.substring(0, 15)}...` : movie.title
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, 10) || []; // Only show top 10 most viewed movies

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Movies
              </Typography>
              <Typography variant="h4">
                {analytics?.totals?.totalMovies || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Views
              </Typography>
              <Typography variant="h4">
                {analytics?.totals?.totalViews || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Downloads
              </Typography>
              <Typography variant="h4">
                {analytics?.totals?.totalDownloads || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Rating
              </Typography>
              <Typography variant="h4">
                {(analytics?.totals?.avgRating || 0).toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Uploads Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Uploads
            </Typography>
            <Box sx={{ height: 300 }}>
              <BarChart
                width={500}
                height={300}
                data={monthlyData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="uploads" fill={theme.palette.primary.main} />
              </BarChart>
            </Box>
          </Paper>
        </Grid>

        {/* Movie Views Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top 10 Most Viewed Movies
            </Typography>
            <Box sx={{ 
              width: '100%', 
              height: 300,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden'
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={viewsData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="title"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={{ 
                      fontSize: 12,
                      fill: theme.palette.text.primary 
                    }}
                  />
                  <YAxis 
                    tick={{ 
                      fontSize: 12,
                      fill: theme.palette.text.primary 
                    }}
                    width={50}
                    label={{ 
                      value: 'Views', 
                      angle: -90, 
                      position: 'insideLeft',
                      fill: theme.palette.text.primary
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                    formatter={(value, name) => [value, 'Views']}
                    labelStyle={{
                      color: theme.palette.text.primary,
                      fontWeight: 'bold'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 4, fill: theme.palette.primary.main }}
                    activeDot={{ r: 8, fill: theme.palette.primary.light }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Categories Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Movies by Category
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              <PieChart width={400} height={300}>
                <Pie
                  data={categoryData}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Box>
          </Paper>
        </Grid>

        {/* Genres Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Movies by Genre
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
              <PieChart width={400} height={300}>
                <Pie
                  data={genreData}
                  dataKey="count"
                  nameKey="genre"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {genreData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Box>
          </Paper>
        </Grid>

        {/* Popular Movies Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Popular Movies
            </Typography>
            <Box>
              {analytics?.popularMovies?.map((movie, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1,
                    borderBottom: index < analytics.popularMovies.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  <Typography>{movie.title}</Typography>
                  <Box>
                    <Typography component="span" sx={{ mr: 2 }}>
                      Views: {movie.views}
                    </Typography>
                    <Typography component="span" sx={{ mr: 2 }}>
                      Downloads: {movie.downloads}
                    </Typography>
                    <Typography component="span">
                      Rating: {movie.rating.toFixed(1)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MovieAnalytics;
